import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Select, Input, EmptyState, PageHeader, StatusBadge, Pagination,
  IconButton, CardSkeleton, StatCard, formatINR,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { generateInvoicePDF } from "../utils/invoicePDF";
import { SiteSearchPopup } from "../components/SiteSearchPopup";
import ClientSelectPopup from "../components/ClientSelectPopup";
import {
  Plus, Printer, Pencil, Trash2, Receipt, MapPin, Lock, Send,
  CircleCheckBig, Eye, TriangleAlert, Wallet,
} from "lucide-react";

const PER_PAGE = 20;

export default function AdminBills() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "",
    month: new Date().toISOString().slice(0, 7),
    clientId: searchParams.get("clientId") || "",
    siteId: searchParams.get("siteId") || "",
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: PER_PAGE });
      if (filters.status) p.set("status", filters.status);
      if (filters.month) p.set("month", filters.month);
      if (filters.clientId) p.set("clientId", filters.clientId);
      if (filters.siteId) p.set("siteId", filters.siteId);
      const { data } = await api.get(`/bills?${p}`);
      setBills(data.data.bills || []);
      setTotal(data.data.total);
    } catch { toast.error("Failed to load bills"); }
    finally { setLoading(false); }
  }, [filters, page]);

  const fetchSummary = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (filters.month) p.set("month", filters.month);
      const { data } = await api.get(`/bills/summary?${p}`);
      setSummary(data.data);
    } catch { /* summary is best-effort */ }
  }, [filters.month]);

  useEffect(() => { fetchBills(); fetchSummary(); }, [fetchBills, fetchSummary]);

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/bills/${id}/status`, { status });
      toast.success(`Bill ${status}`);
      fetchBills(); fetchSummary();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this bill?")) return;
    try {
      await api.delete(`/bills/${id}`);
      toast.success("Bill deleted");
      fetchBills(); fetchSummary();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
  };

  const handlePDF = async (bill) => {
    try {
      const { data } = await api.get(`/bills/${bill._id}`);
      generateInvoicePDF(data.data, { signatureUrl: user?.signatureUrl, sealUrl: user?.sealUrl });
    } catch { toast.error("Failed to generate PDF"); }
  };

  const ff = (k) => ({ value: filters[k], onChange: (e) => { setFilters({ ...filters, [k]: e.target.value }); setPage(1); } });
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  const billed = (summary?.revenue?.draft || 0) + (summary?.revenue?.sent || 0) + (summary?.revenue?.overdue || 0) + (summary?.revenue?.paid || 0);
  const outstanding = (summary?.revenue?.sent || 0) + (summary?.revenue?.overdue || 0);

  return (
    <AdminLayout>
      <div className="space-y-5">
        <PageHeader
          title="Invoices & Billing"
          subtitle="Client invoices with GST support"
          action={<Button to="/admin/bills/new" icon={<Plus size={16} />} className="w-full sm:w-auto">Create Invoice</Button>}
        />

        {/* ══ Month summary ══ */}
        {summary && (
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
            <StatCard icon={<Receipt size={17} />} color="slate" label="Total billed" value={formatINR(billed, { compact: true })} />
            <StatCard icon={<CircleCheckBig size={17} />} color="emerald" label="Paid" value={formatINR(summary?.revenue?.paid || 0, { compact: true })} />
            <StatCard icon={<Wallet size={17} />} color="amber" label="Outstanding" value={formatINR(outstanding, { compact: true })} />
            <StatCard icon={<TriangleAlert size={17} />} color="red" label="Overdue bills" value={summary?.summary?.overdue || 0} />
          </div>
        )}

        {/* ══ Filters ══ */}
        <Card className="p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <Input label="Month" type="month" {...ff("month")} />
            <Select label="Status" {...ff("status")}>
              <option value="">All Status</option>
              {["draft", "sent", "paid", "overdue", "cancelled"].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
            </Select>
            <ClientSelectPopup
              label="Client" placeholder="All Clients" value={filters.clientId}
              onChange={(id) => { setFilters({ ...filters, clientId: id }); setPage(1); }}
            />
            <SiteSearchPopup
              label="Site" placeholder="All Sites" value={filters.siteId}
              onChange={(v) => { setFilters({ ...filters, siteId: v }); setPage(1); }}
            />
          </div>
        </Card>

        {loading ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} lines={2} />)}</div>
        ) : bills.length === 0 ? (
          <Card>
            <EmptyState icon={<Receipt size={22} />} title="No invoices found"
              description="Nothing matches these filters. Try another month or clear the status filter."
              action={<Button to="/admin/bills/new" icon={<Plus size={16} />}>Create Invoice</Button>} />
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {bills.map((bill) => (
                <Card key={bill._id} className="p-4" hover>
                  {/* actions lead the row so they're the first thing in reach */}
                  <div className="flex items-center gap-1.5 flex-wrap pb-3 mb-3 border-b border-ink-100 lg:hidden">
                    {bill.status === "draft" && (
                      <Button size="xs" variant="soft" icon={<Send size={13} />} onClick={() => handleStatusChange(bill._id, "sent")}>Send</Button>
                    )}
                    {(bill.status === "sent" || bill.status === "overdue") && (
                      <Button size="xs" variant="soft" to={`/admin/bills/${bill._id}`} icon={<CircleCheckBig size={13} />}
                        className="!bg-emerald-50 !text-emerald-700 hover:!bg-emerald-100">Mark Paid</Button>
                    )}
                    <IconButton size="sm" variant="outline" icon={<Printer size={15} />} onClick={() => handlePDF(bill)} title="Download PDF" />
                    <IconButton size="sm" variant="outline" icon={<Eye size={15} />} to={`/admin/bills/${bill._id}`} title="View invoice" />
                    {!bill.isLocked && <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} to={`/admin/bills/${bill._id}/edit`} title="Edit" />}
                    {!bill.isLocked && <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(bill._id)} title="Delete" className="ml-auto" />}
                  </div>

                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">

                    {/* actions — first column on wide rows too */}
                    <div className="hidden lg:flex items-center gap-1.5 shrink-0 order-first">
                      <IconButton size="sm" variant="outline" icon={<Printer size={15} />} onClick={() => handlePDF(bill)} title="Download PDF" />
                      <IconButton size="sm" variant="outline" icon={<Eye size={15} />} to={`/admin/bills/${bill._id}`} title="View invoice" />
                      {!bill.isLocked && <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} to={`/admin/bills/${bill._id}/edit`} title="Edit" />}
                      {!bill.isLocked && <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(bill._id)} title="Delete" />}
                    </div>

                    {/* identity */}
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <span className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 grid place-items-center shrink-0">
                        <Receipt size={17} />
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/admin/bills/${bill._id}`} className="font-semibold text-ink-900 hover:text-brand-600 transition-colors truncate">
                            {bill.clientId?.name || "—"}
                          </Link>
                          <span className="font-mono text-[11px] text-ink-400">{bill.billNumber}</span>
                          {bill.isLocked && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-600" title="Locked">
                              <Lock size={11} /> Locked
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-400 mt-1">
                          <span>{bill.billDate}</span>
                          {bill.siteId?.name && (
                            <span className="inline-flex items-center gap-1 min-w-0">
                              <MapPin size={11} className="shrink-0" /><span className="truncate max-w-[180px]">{bill.siteId.name}</span>
                            </span>
                          )}
                          {bill.subject && <span className="truncate max-w-[220px]">{bill.subject}</span>}
                          {bill.gstType && bill.gstType !== "none" && (
                            <span className="text-indigo-600 font-medium">
                              {bill.gstType === "cgst_sgst" ? `CGST+SGST ${bill.gstRate}%` : `IGST ${bill.gstRate}%`}
                            </span>
                          )}
                          {bill.status === "paid" && bill.paidDate && (
                            <span className="text-emerald-600 font-medium">Paid {bill.paidDate}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* money + status */}
                    <div className="flex items-center justify-between lg:justify-end gap-3 lg:gap-5 shrink-0">
                      <StatusBadge status={bill.status} />
                      <div className="text-right">
                        <div className="text-lg font-bold text-ink-900 font-display tnum leading-none">
                          {formatINR(bill.grandTotal)}
                        </div>
                        {bill.paidAmount > 0 && bill.status !== "paid" && (
                          <div className="text-[11px] text-emerald-600 mt-1 tnum">{formatINR(bill.paidAmount)} received</div>
                        )}
                      </div>
                    </div>

                    {/* the one state-changing action stays next to the money it refers to */}
                    <div className="hidden lg:flex items-center gap-1.5 shrink-0">
                      {bill.status === "draft" && (
                        <Button size="xs" variant="soft" icon={<Send size={13} />} onClick={() => handleStatusChange(bill._id, "sent")}>Send</Button>
                      )}
                      {(bill.status === "sent" || bill.status === "overdue") && (
                        <Button size="xs" variant="soft" to={`/admin/bills/${bill._id}`}
                          icon={<CircleCheckBig size={13} />}
                          className="!bg-emerald-50 !text-emerald-700 hover:!bg-emerald-100">Mark Paid</Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {pages > 1 && (
              <Card><Pagination page={page} pages={pages} total={total} limit={PER_PAGE} onPage={setPage} /></Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
